/**
 * `_app.tsx` bootstraps Crisp by setting `window.$crisp = []` and then async-loading
 * `client.crisp.chat/l.js`, which swaps that bootstrap queue for the real client. When the
 * script never loads — corporate egress filtering, an ad blocker, an offline tab — the plain
 * array survives and every `$crisp.push(...)` is queued into something nothing will ever
 * drain: no error, no chat, and a dead "Live Chat" control for the user. Detect that and let
 * the app offer the email fallback instead of failing silently.
 */
export const LIVE_CHAT_UNAVAILABLE_EVENT = 'hookdeck:live-chat-unavailable';

const CRISP_READY_POLL_INTERVAL_MS = 150;
const CRISP_READY_TIMEOUT_MS = 1500;

export const isChatAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  const crisp = (window as any).$crisp;
  return !!crisp && !Array.isArray(crisp);
};

/**
 * Give a slow-but-loading widget a moment before declaring it unavailable, so a click made
 * seconds after page load doesn't wrongly fall back to email.
 */
const waitForChat = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (isChatAvailable()) {
      resolve(true);
      return;
    }

    const started_at = Date.now();
    const interval = setInterval(() => {
      if (isChatAvailable()) {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - started_at >= CRISP_READY_TIMEOUT_MS) {
        clearInterval(interval);
        resolve(false);
      }
    }, CRISP_READY_POLL_INTERVAL_MS);
  });

export const showChat = async (): Promise<boolean> => {
  const is_available = await waitForChat();

  if (!is_available) {
    window.dispatchEvent(new CustomEvent(LIVE_CHAT_UNAVAILABLE_EVENT));
    return false;
  }

  const { $crisp } = window as any;
  $crisp.push(['do', 'chat:show']);
  // Crisp library has a race condition if chat:show and chat:open is called right after another.
  setTimeout(() => {
    $crisp.push(['do', 'chat:open']);
  }, 100);
  return true;
};
